import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { Button, Modal, spacingHelper, Stack, Text, MOBILE_MAX } from '@property.com.au/ui-kit';
import { convertHexStringToRGBA } from '../../../app-kit/helpers/convertHexStringToRGBA';
import type { PropertyRelationship } from '../PropertyRelationship';

import styled, { useTheme } from 'styled-components';
import { UpdateRelationshipModalContent } from '../UpdateRelationshipModalContent/UpdateRelationshipModalContent';
import type {
  AerialImage,
  ListingImage,
} from '../UpdateRelationshipModalContent/subcomponents/FollowPropertyImage/FollowPropertyImage.types';
import { LoadingDots } from '../../../app-kit/components/LoadingDots/LoadingDots';
import { ModalDialog } from '../../ModalDialog/ModalDialog';
import { getCssVariableValue } from '../../../app-kit/helpers/getCssVariableValue';

const StyledStack = styled(Stack)`
  margin-right: ${spacingHelper('large')};
`;

interface FollowPropertyModalProps {
  title: string;
  subtitle: string;
  image?: ListingImage | AerialImage | null;
  opened: boolean;
  onClose: () => void;
  onSubmit: (propertyRelationship: PropertyRelationship) => void;
  loading: boolean;
  defaultSelection: PropertyRelationship;
}

export const FollowPropertyModal: FunctionComponent<FollowPropertyModalProps> = ({
  title,
  subtitle,
  image,
  opened,
  onClose,
  onSubmit,
  loading,
  defaultSelection,
}) => {
  const [propertyRelationship, setPropertyRelationship] =
    useState<PropertyRelationship>(defaultSelection);

  const theme = useTheme();
  const colourValue = getCssVariableValue(theme.palette.backgroundOverlayStatic);
  const backgroundColor = convertHexStringToRGBA(colourValue, 0.3);

  return (
    <Modal
      opened={opened}
      onRequestClose={onClose}
      getAppElement={() => '#argonaut-wrapper'}
      reactModalProps={{
        style: {
          overlay: {
            backgroundColor,
          },
        },
      }}
    >
      <ModalDialog
        autoFullScreen
        fullScreenMediaQuery={`(max-width: ${MOBILE_MAX}px)`}
        onClose={onClose}
        header={
          <StyledStack gap="0.125rem">
            <Text as="h2" variant="subtitle01">
              {title}
            </Text>
            <Text as="h3" variant="body01">
              {subtitle}
            </Text>
          </StyledStack>
        }
        footer={
          <Button
            data-testid="follow-modal-set-relationship-button"
            sizeVariant="medium"
            onClick={() => onSubmit(propertyRelationship)}
            fullWidth
            disabled={loading}
          >
            {loading ? <LoadingDots /> : 'Set relationship'}
          </Button>
        }
      >
        <UpdateRelationshipModalContent
          image={image}
          propertyRelationship={propertyRelationship}
          privacyLinkOutboundLinkType="set-relationship-modal-privacy-link"
          onSelectionChange={setPropertyRelationship}
        />
      </ModalDialog>
    </Modal>
  );
};
